// =============================================================================
// Bureau de Matthieu « pour les nuls » (T-392) — UNE page, trois cases, des phrases simples.
//   À faire · Fait aujourd'hui · Mis de côté — rien à faire
// Toute la lecture vient d'UNE route (`/simple`) : les trois nombres sont comptés sur la même liste. Aucune décision ici : l'écran affiche ce que
// le serveur propose ; rien ne part sans le clic de la personne (le serveur impose version + empreinte du texte vu).
// T-438 : cartes rangées par nature (groupes repliables) ; sur ordinateur (≥ 1024 px) vue partagée liste (≈ 40 %) / détail de la carte choisie ;
// sur téléphone la liste, puis le détail en plein écran avec « Retour ».
// Charte : jetons var(--oh-*) uniquement (2 modes), boutons de la charte (1 principal par carte), lisible à 390 px.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Inbox, MessageSquare, Archive, ExternalLink, RefreshCw, Send, ChevronDown, ChevronRight, ArrowLeft, Ban } from "lucide-react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { OhInput, OhStatusNote } from "../../../components/ui/oh-charte";
import Bouton from "./boutonBureau";
import ParlerAMatthieu from "./ParlerAMatthieu";
import { FicheAmo } from "./BureauAmo";
import { grouperCartes, aPlat, phraseDuJour, NATURES, groupeDe } from "./bureauOrdre";
import MailOriginePanneau, { BoutonMailOrigine } from "./MailOriginePanneau";
import * as api from "./bureauAmoApi";
import { agentApi } from "./bureauAmoApi";
import { LigneApprendre, MatthieuAppris, PanneauRattacher, PanneauTransmettre, PropositionApprise, RetourListe, TousLesMails } from "./BureauOptions";

const muted = { color: "var(--oh-text-muted)", fontSize: 14, lineHeight: 1.6 };
const CASES = [
  { cle: "a_faire", compteur: "a_faire", libelle: "À faire", court: "À faire", icone: Inbox },
  { cle: "fait_aujourdhui", compteur: "fait_aujourdhui", libelle: "Fait aujourd'hui", court: "Fait aujourd'hui", icone: CheckCircle2 },
  { cle: "de_cote", compteur: "de_cote", libelle: "Mis de côté — rien à faire", court: "De côté", icone: Archive },
];

export const PHRASE_ACCUEIL = "Matthieu lit la boîte amo@ et prépare le travail. Ici, tu valides. Rien ne part sans ton clic.";

/** Phrase de la case vide — dite avec les mots de la personne, jamais un « 0 résultat ». */
export function phraseVide(cle, compteurs, phraseAgent) {
  const deCote = compteurs?.de_cote || 0;
  if (cle === "a_faire" && phraseAgent) return phraseAgent;       // T-406 : « Aucun dossier ne demande l'avance aujourd'hui »
  if (cle === "a_faire") {
    return deCote > 0
      ? `Rien à faire. Matthieu a mis de côté ${deCote} mail${deCote > 1 ? "s" : ""} sans intérêt.`
      : "Rien à faire. Tout est à jour.";
  }
  if (cle === "fait_aujourdhui") return "Rien de fait pour l'instant aujourd'hui.";
  return "Rien n'a été mis de côté.";
}

const formatQuand = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

/** T-438 : vue partagée à partir de 1024 px (largeur de la fenêtre). */
export const LARGEUR_SPLIT = 1024;
export const HAUTEUR_MIN = 360;
const largeurFenetre = () => (typeof window === "undefined" ? LARGEUR_SPLIT : window.innerWidth);
function useGrandEcran() {
  const [grand, setGrand] = useState(() => largeurFenetre() >= LARGEUR_SPLIT);
  useEffect(() => {
    const maj = () => setGrand(largeurFenetre() >= LARGEUR_SPLIT);
    window.addEventListener("resize", maj);
    maj();
    return () => window.removeEventListener("resize", maj);
  }, []);
  return grand;
}

/** T-438 : une ligne de la liste (nature, qui, quoi, depuis quand) ; le détail et les boutons sont dans la carte ouverte. */
function Ligne({ c, nature, actif, onChoisir }) {
  const Icone = nature?.icone;
  const quand = c.case === "fait" ? c.quand : c.date;
  return (
    <li style={{ listStyle: "none" }}>
      <button type="button" data-testid={`bureau-simple-ligne-${c.ref}`} data-focus={actif ? "oui" : "non"} aria-current={actif ? "true" : undefined} onClick={onChoisir}
        style={{ width: "100%", textAlign: "left", cursor: "pointer", font: "inherit", padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-card)", minHeight: 56,
          background: actif ? "var(--oh-accent-bg)" : "var(--oh-bg-elevated)", color: actif ? "var(--oh-accent-text)" : "var(--oh-text)",
          border: `${actif ? 1 : 0.5}px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`, display: "flex", gap: "var(--oh-space-3)", alignItems: "flex-start" }}>
        {Icone && <Icone className="w-4 h-4 shrink-0" aria-hidden="true" style={{ marginTop: 4 }} />}
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 8 }}>
            <span style={{ fontWeight: 500, fontSize: 15 }} data-testid="bureau-simple-qui-ligne">{c.qui}</span>
            <span style={{ fontSize: 14, color: actif ? "var(--oh-accent-text)" : "var(--oh-text-muted)" }}>{c.quoi}</span>
            {c.urgence === "urgent" && <span style={{ fontSize: 12, fontWeight: 500, padding: "2px 8px", borderRadius: 8, background: "var(--oh-stat-red-bg)", color: "var(--oh-bm-puce-red-text)", border: "0.5px solid var(--oh-stat-red-border)" }}>Urgent</span>}
          </span>
          {quand && <span style={{ display: "block", fontSize: 13, color: actif ? "var(--oh-accent-text)" : "var(--oh-text-muted)" }}>{formatQuand(quand)}</span>}
        </span>
      </button>
    </li>
  );
}

function Carte({ c, focus, onAction, occupe, onFocus, onOuvrirDossier, onVoirMail, agent = "matthieu", nomAgent = "Matthieu", motifs = [], onDemander }) {   // T-459 : + Indésirable / Inutile / Transmettre à… / proposition apprise
  const [confirme, setConfirme] = useState(false);
  const [raison, setRaison] = useState(null);          // T-406 : « Mettre de côté » chez Chloé / Joey demande une raison (Refuser = mettre de côté avec une raison)
  const [transmettre, setTransmettre] = useState(null);  // T-406 : « Transmettre au dossier » (Matthieu) : null | motif choisi
  const estMatthieu = agent === "matthieu";
  const refMail = c.mail_ref || (estMatthieu ? c.ref : null);
  const [detail, setDetail] = useState(false);
  const [ignoreAppris, setIgnoreAppris] = useState(false);
  const unMail = estMatthieu && ["fiche", "entrant", "ag04"].includes(c.source);
  const b = c.bouton || {};
  const aConfirmer = Boolean(b.destinataire_a_confirmer);
  const bloque = occupe || (b.action === "envoyer" && aConfirmer && !confirme) || b.action === "attendre";
  const lien = c.dossier?.reference ? `Ouvrir le dossier ${c.dossier.reference}` : null;

  return (
    <li data-testid={`bureau-simple-carte-${c.ref}`} data-focus={focus ? "oui" : "non"} onClick={onFocus}
      style={{ listStyle: "none", padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", borderRadius: "var(--oh-radius-card)",
        border: `${focus ? 1 : 0.5}px solid ${focus ? "var(--oh-accent)" : "var(--oh-border)"}` }}>
      <div className="flex flex-wrap items-baseline gap-2">
        <span style={{ fontWeight: 500, fontSize: 16, color: "var(--oh-text)" }} data-testid="bureau-simple-qui">{c.qui}</span>
        <span style={muted} data-testid="bureau-simple-quoi">{c.quoi}</span>
        {c.urgence === "urgent" && (
          <span style={{ fontSize: 12, fontWeight: 500, padding: "2px 8px", borderRadius: 8, background: "var(--oh-stat-red-bg)", color: "var(--oh-bm-puce-red-text)", border: "0.5px solid var(--oh-stat-red-border)" }}>Urgent</span>
        )}
      </div>
      {c.objet && <p style={{ ...muted, margin: "4px 0 0", overflowWrap: "anywhere" }}>« {c.objet} »</p>}
      {c.case === "a_faire" && (c.date || c.dossier?.reference) && (
        <p style={{ ...muted, margin: "4px 0 0" }} data-testid="bureau-simple-depuis">
          {c.date ? `Reçu le ${formatQuand(c.date)}` : ""}{c.date && c.dossier?.reference ? " · " : ""}{c.dossier?.reference ? `Dossier ${c.dossier.reference}` : ""}
        </p>
      )}

      {c.case === "a_faire" && (
        <>
          <p style={{ margin: "8px 0 0", color: "var(--oh-text)", fontSize: 15, lineHeight: 1.6, overflowWrap: "anywhere" }} data-testid="bureau-simple-propose">{c.propose}</p>
          {estMatthieu && c.appris && !ignoreAppris && (
            <PropositionApprise appris={c.appris} occupe={occupe} onFaire={() => onAction(c, { action: "appris_faire", appris: c.appris })} onIgnorer={() => setIgnoreAppris(true)} />
          )}
          {c.rappel && (
            <div style={{ marginTop: 8 }} data-testid="bureau-simple-rappel">
              <p style={{ margin: 0, color: "var(--oh-text)", fontSize: 15 }} data-testid="bureau-simple-rappel-numero">Numéro à rappeler : {c.rappel.numero}</p>
              {c.rappel.numero_fiche && <p style={{ ...muted, margin: "4px 0 0" }} data-testid="bureau-simple-rappel-numero-fiche">Numéro différent de celui de la fiche client ({c.rappel.numero_fiche}).</p>}
              {c.rappel.corrections > 0 && <p style={{ ...muted, margin: "4px 0 0" }}>Coordonnées corrigées par la personne ({c.rappel.corrections} fois).</p>}
              {c.rappel.opposition && (
                <div style={{ marginTop: 8 }}>
                  <OhStatusNote variant="info" title="Opposé à la prospection" data-testid="bureau-simple-rappel-opposition">{c.rappel.note_opposition}</OhStatusNote>
                </div>
              )}
            </div>
          )}
          {c.texte && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: "pointer", color: "var(--oh-accent-text)", fontSize: 14 }}>Voir toute la réponse</summary>
              <pre style={{ whiteSpace: "pre-wrap", margin: "8px 0 0", fontFamily: "inherit", fontSize: 14, color: "var(--oh-text)", background: "var(--oh-bg-surface)",
                border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)", padding: "var(--oh-space-3)" }}>{c.texte}</pre>
            </details>
          )}
          {aConfirmer && b.action === "envoyer" && (
            <label className="flex items-center gap-2" style={{ marginTop: 8, fontSize: 14, color: "var(--oh-text)" }}>
              <input type="checkbox" data-testid="bureau-simple-confirmer" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} />
              <span>La réponse part à une autre adresse que celle du mail : je confirme <strong>{b.destinataire_a_confirmer}</strong></span>
            </label>
          )}
        </>
      )}
      {c.case === "fait" && (
        <p style={{ margin: "8px 0 0", color: "var(--oh-text)", fontSize: 15 }} data-testid="bureau-simple-fait">
          {c.fait}{c.par ? ` · par ${c.par}` : ""}{c.quand ? ` · ${formatQuand(c.quand)}` : ""}
        </p>
      )}
      {c.case === "de_cote" && (
        <p style={{ margin: "8px 0 0", color: "var(--oh-text)", fontSize: 15 }} data-testid="bureau-simple-raison">{c.raison_de_cote}</p>
      )}

      {c.case === "a_faire" && c.bouton?.action === "trancher" ? (
        <div className="flex flex-wrap gap-2" style={{ marginTop: 12 }} role="group" aria-label="Réponses possibles">
          {(b.choix || []).map((ch) => (
            <Bouton key={ch.id} type="primaire" disabled={occupe} data-testid={`bureau-simple-choix-${ch.id}`} style={{ height: 44 }}
              onClick={() => onAction(c, { action: "trancher", choix_id: ch.id })}>{ch.libelle}</Bouton>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 12 }}>
          {/* T-392b : jamais de bouton sans texte (les cartes « fait » n'ont pas d'action) ; dans « Mis de côté », « Remettre à faire » reste discret */}
          {b.libelle && (
            <Bouton type={c.case === "de_cote" ? "secondaire" : "primaire"} disabled={bloque} data-testid="bureau-simple-principal"
              style={{ height: 44, padding: "0 var(--oh-space-5, 20px)", fontSize: 16 }}
              onClick={() => onAction(c, { ...b, destinataire_confirme: confirme ? b.destinataire_a_confirmer : undefined })}>{b.libelle}</Bouton>
          )}
          {c.secondaire && (
            <Bouton type="secondaire" disabled={occupe} data-testid="bureau-simple-secondaire"
              onClick={() => {
                if (c.secondaire.action === "voir") setDetail((v) => !v);
                else if (!estMatthieu && c.secondaire.action === "mettre_de_cote") setRaison((r) => (r === null ? "" : null));
                else onAction(c, c.secondaire);
              }}>{c.secondaire.libelle}</Bouton>
          )}
          {estMatthieu && lien && onOuvrirDossier && (          // T-406b : chez Chloé et Joey le bouton principal ouvre déjà le dossier (une carte = une action)
            <Bouton type="discret" data-testid="bureau-simple-dossier" aria-label={lien} onClick={(e) => { e.stopPropagation(); onOuvrirDossier(c.dossier.id); }}>
              <ExternalLink className="w-4 h-4 inline mr-1" aria-hidden="true" />{lien}
            </Bouton>
          )}
        </div>
      )}
      {/* T-404 : sur toutes les cartes (À faire, Fait, Mis de côté), jamais caché dans un repli */}
      {raison !== null && (
        <div style={{ marginTop: 8 }} className="flex flex-wrap items-center gap-2" data-testid="bureau-simple-raison-saisie">
          <OhInput aria-label="Pourquoi mettre cette carte de côté ?" placeholder="Pourquoi ? (quelques mots)" value={raison} maxLength={200} data-testid="bureau-simple-raison-champ"
            onChange={(e) => setRaison(e.target.value)} style={{ flex: "1 1 200px" }} />
          <Bouton type="secondaire" disabled={occupe || raison.trim().length < 3} data-testid="bureau-simple-raison-confirmer" style={{ height: 44 }}
            onClick={() => { onAction(c, { action: "mettre_de_cote", raison: raison.trim() }); setRaison(null); }}>Mettre de côté</Bouton>
        </div>
      )}
      <div style={{ marginTop: 8 }} className="flex flex-wrap items-center gap-2">
        {/* T-404 / T-406 : sur toute carte venue d'un mail, jamais caché dans un repli */}
        {refMail && <BoutonMailOrigine refCarte={refMail} onVoir={onVoirMail} />}
        {onDemander && (estMatthieu ? c.source === "fiche" : Boolean(c.dossier?.id)) && (
          <Bouton type="discret" disabled={occupe} data-testid="bureau-simple-demander" aria-label={`Demander à ${nomAgent} au sujet de cette carte`}
            onClick={(e) => { e.stopPropagation(); onDemander(c); }}>
            <MessageSquare className="w-4 h-4 inline mr-1" aria-hidden="true" />Demander à {nomAgent}
          </Bouton>
        )}
        {unMail && c.case !== "fait" && c.etat !== "indesirable" && (
          <Bouton type="discret" disabled={occupe} data-testid="bureau-simple-indesirable" aria-label="Marquer ce mail indésirable (rangé dans Indésirables, jamais effacé)"
            onClick={(e) => { e.stopPropagation(); onAction(c, { action: "indesirable" }); }}>
            <Ban className="w-4 h-4 inline mr-1" aria-hidden="true" />Indésirable
          </Bouton>
        )}
        {unMail && c.case === "a_faire" && (
          <Bouton type="discret" disabled={occupe} data-testid="bureau-simple-inutile" aria-label="Classer ce mail sans réponse"
            onClick={(e) => { e.stopPropagation(); onAction(c, { action: "inutile" }); }}>
            <Archive className="w-4 h-4 inline mr-1" aria-hidden="true" />Inutile, classer
          </Bouton>
        )}
        {unMail && c.dossier?.id && c.case !== "de_cote" && (
          <Bouton type="discret" disabled={occupe} data-testid="bureau-simple-transmettre" onClick={() => setTransmettre((t) => (t === null ? "" : null))}>
            <Send className="w-4 h-4 inline mr-1" aria-hidden="true" />Transmettre à…
          </Bouton>
        )}
      </div>
      {transmettre !== null && (
        <PanneauTransmettre refs={[c.ref]} motifs={motifs} occupe={occupe} onFermer={() => setTransmettre(null)}
          onConfirmer={(v) => { onAction(c, { action: "transmettre", ...v }); setTransmettre(null); }} />
      )}
      {detail && (
        <p style={{ ...muted, margin: "8px 0 0" }} data-testid="bureau-simple-detail">
          {c.objet ? `Objet : « ${c.objet} ». ` : ""}{c.date ? `Reçu le ${String(c.date).slice(0, 10)}. ` : ""}
          Ce mail n'a pas de réponse préparée : tu peux le mettre de côté, ou en parler à Matthieu.
        </p>
      )}
    </li>
  );
}

export default function BureauSimple({ onOuvrirWebmail, onOuvrirDossier, agent = "matthieu" }) {
  const estMatthieu = agent === "matthieu";
  const lien = useMemo(() => (estMatthieu ? { ...api, simple: agentApi("matthieu").simple, remettre: api.remettreAFaire } : { ...agentApi(agent), remettre: agentApi(agent).remettreAFaire }), [agent, estMatthieu]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [case_, setCase] = useState("a_faire");
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [parler, setParler] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [demande, setDemande] = useState(null);   // T-406 : carte sur laquelle on « Demande à <agent> »
  const [mailVu, setMailVu] = useState(null);
  const [focus, setFocus] = useState(0);
  const [replies, setReplies] = useState({});     // T-438 : groupes repliés (clé = case + groupe)
  const [ouvert, setOuvert] = useState(false);    // T-438 : sur téléphone, le détail est ouvert en plein écran
  const grand = useGrandEcran();
  const [vue, setVue] = useState("bureau");          // T-459 : "bureau" | "tous" (Tous les mails) | "appris" (Ce que Matthieu a appris) — Matthieu seulement
  const [tous, setTous] = useState({ donnees: null, erreur: null, chargement: false });
  const [carteTous, setCarteTous] = useState(null);   // T-459 : carte ouverte à côté de la grille « Tous les mails »
  const [groupe, setGroupe] = useState(null);         // T-459 : action de groupe en cours de saisie : { type: "transmettre" | "rattacher", refs }
  const [aApprendre, setAApprendre] = useState(null); // T-459 : « À l'avenir, faire pareil ? » après une décision
  const [texteAppris, setTexteAppris] = useState(null);
  const refSplit = useRef(null);
  const [hauteur, setHauteur] = useState(null);   // T-438b : hauteur de la vue partagée = ce qui reste d'écran sous son bord haut
  const numero = useRef(0);

  const charger = useCallback(async () => {
    const n = ++numero.current;
    try {
      const d = await lien.simple();
      if (n === numero.current) { setDonnees(d); setErreur(null); }
    } catch (e) {
      if (n === numero.current) setErreur(typeof e.detail === "string" ? e.detail : "Réessayez dans un instant.");
    }
  }, [lien]);
  useEffect(() => { charger(); }, [charger]);

  const chargerTous = useCallback(async () => {
    setTous((t) => ({ ...t, chargement: true }));
    try { setTous({ donnees: await api.tousLesMails(), erreur: null, chargement: false }); }
    catch (e) { setTous({ donnees: null, erreur: typeof e.detail === "string" ? e.detail : "Réessayez dans un instant.", chargement: false }); }
  }, []);
  useEffect(() => { if (estMatthieu && vue === "tous") chargerTous(); }, [estMatthieu, vue, chargerTous]);
  const rafraichir = useCallback(async () => { await charger(); if (estMatthieu && vue === "tous") await chargerTous(); }, [charger, chargerTous, estMatthieu, vue]);

  // T-459 : indésirable / inutile, sur une carte ou un groupe. Un résultat PAR mail : les protégés sont refusés avec leur raison, les autres passent.
  const annuler = useCallback(async (refs) => {
    try { for (const r of refs) await api.remettreAFaire(r); toast.success("Annulé : le mail est remis à faire."); }
    catch (e) { toast.error(typeof e.detail === "string" ? e.detail : "Annulation impossible."); }
    await rafraichir();
  }, [rafraichir]);
  const marquer = useCallback(async (refs, nature) => {
    const r = await (nature === "indesirable" ? api.indesirable : api.inutile)(refs);
    const bons = (r?.resultats || []).filter((x) => x.ok).map((x) => x.ref);
    const refus = (r?.resultats || []).filter((x) => !x.ok);
    if (refus.length) toast.error(refus.length === 1 ? refus[0].message : `${refus.length} mails refusés. ${refus[0].message}`);
    if (bons.length) {
      toast.success(nature === "indesirable" ? (bons.length > 1 ? `${bons.length} mails rangés dans Indésirables.` : "Mail rangé dans Indésirables.") : (bons.length > 1 ? `${bons.length} mails classés sans réponse.` : "Mail classé sans réponse."),
        { duration: 8000, action: { label: "Annuler", onClick: () => annuler(bons) } });
    }
    return bons;
  }, [annuler]);

  const compteurs = donnees?.compteurs || {};
  const nom = donnees?.agent?.nom || { matthieu: "Matthieu", chloe: "Chloé", joey: "Joey" }[agent] || "l'agent";
  // T-404c : « À faire » est rangé par nature (Répondre, Vérifier, Rattacher, Alertes) ; la liste à plat suit l'ordre vu à l'écran (raccourcis j / k).
  // T-438 : « Fait » et « De côté » sont rangés aussi dès qu'il y a au moins deux natures.
  const groupesBruts = grouperCartes(donnees?.[case_], agent);
  const groupes = case_ === "a_faire" || groupesBruts.length > 1 ? groupesBruts : null;
  const clePli = (g) => `${case_}:${g.cle}`;
  const visibles = groupes ? groupes.map((g) => (replies[clePli(g)] ? { ...g, cartes: [] } : g)) : null;   // une carte dans un groupe replié n'est plus dans l'ordre des flèches
  const liste = visibles ? aPlat(visibles) : (donnees?.[case_] || []);
  const choisie = liste[Math.min(focus, liste.length - 1)] || null;
  const natureDe = (c) => NATURES[groupeDe(c, agent)];

  const compterAppris = (appris) => api.apprisAppliquee(appris.regle_id).catch(() => {});
  const faireAppris = useCallback(async (c, appris) => {
    if (appris.action === "indesirable" || appris.action === "inutile") {
      const bons = await marquer([c.ref], appris.action);
      if (bons.length) await compterAppris(appris);
    } else if (appris.action === "rattacher") {
      await api.rattacher(c.id, appris.params?.dossier_id);
      toast.success("Mail rattaché au dossier."); await compterAppris(appris);
    } else if (appris.action === "transmettre") {
      const r = await api.transmettreGroupe([c.ref], appris.params?.pour, appris.params?.motif);
      const x = r?.resultats?.[0];
      if (x?.ok) { toast.success(`Transmis à ${x.pour === "joey" ? "Joey" : "Chloé"}.`); await compterAppris(appris); } else toast.error(x?.message || "Transmission impossible.");
    } else if (appris.action === "reponse") {
      setTexteAppris(appris.params?.texte || null); setFiche(c); await compterAppris(appris);       // pré-remplit l'éditeur ; rien ne part sans le clic « Modifier puis envoyer »
    }
  }, [marquer]);   // eslint-disable-line react-hooks/exhaustive-deps

  const apprendre = useCallback(async (portee) => {
    if (!aApprendre) return;
    setOccupe(true);
    try {
      await api.apprendre({ ref: aApprendre.ref, action: aApprendre.action, portee, ...(aApprendre.params || {}), ...(aApprendre.texte ? { texte: aApprendre.texte } : {}), ...(portee === "type" && aApprendre.libelleType ? { libelle_type: aApprendre.libelleType } : {}) });
      toast.success("C'est noté. Matthieu le proposera la prochaine fois (tu le retrouves dans « Ce que Matthieu a appris »).");
      setAApprendre(null);
    } catch (e) { toast.error(typeof e.detail === "string" ? e.detail : "Impossible d'enregistrer la règle."); setAApprendre(null); }
    finally { setOccupe(false); }
  }, [aApprendre]);

  // Actions de groupe de « Tous les mails »
  const surGroupe = useCallback(async (type, refs) => {
    if (type === "transmettre" || type === "rattacher") { setGroupe({ type, refs }); return; }
    setOccupe(true);
    try {
      if (type === "indesirable" || type === "inutile") await marquer(refs, type);
      else if (type === "de_cote") { for (const r of refs) await api.mettreDeCote(r, "Mis de côté en groupe"); toast.success("Mis de côté. Tu peux les remettre à faire."); }
      else if (type === "remettre") { for (const r of refs) await api.remettreAFaire(r); toast.success("Remis à faire."); }
      await rafraichir();
    } catch (e) { toast.error(typeof e.detail === "string" ? e.detail : "Action impossible, réessayez."); }
    finally { setOccupe(false); }
  }, [marquer, rafraichir]);
  const confirmerGroupe = useCallback(async (v) => {
    setOccupe(true);
    try {
      const r = groupe.type === "transmettre" ? await api.transmettreGroupe(groupe.refs, v.pour, v.motif, v.note) : await api.rattacherGroupe(groupe.refs, v);
      const refus = (r?.resultats || []).filter((x) => !x.ok);
      if (refus.length) toast.error(refus.length === 1 ? refus[0].message : `${refus.length} mails refusés. ${refus[0].message}`);
      if (r?.ok) toast.success(groupe.type === "transmettre" ? `${r.ok} mail${r.ok > 1 ? "s" : ""} transmis à ${v.pour === "joey" ? "Joey" : "Chloé"}.` : `${r.ok} mail${r.ok > 1 ? "s" : ""} rattaché${r.ok > 1 ? "s" : ""}.`);
      setGroupe(null);
      await rafraichir();
    } catch (e) { toast.error(typeof e.detail === "string" ? e.detail : "Action impossible, réessayez."); }
    finally { setOccupe(false); }
  }, [groupe, rafraichir]);

  const agir = useCallback(async (c, a) => {
    if (occupe || !a) return;
    const faire = {
      envoyer: () => api.valider(c.id, c.version, a.sha, a.destinataire_confirme).then((r) => toast.success(
        r?.mode === "reel" ? "Réponse envoyée." : "Réponse enregistrée : l'envoi est en essai, rien n'est parti.")),
      rattacher: () => api.rattacher(c.id, a.dossier_id).then(() => { toast.success("Mail rattaché au dossier."); setAApprendre({ ref: c.ref, action: "rattacher", params: { dossier_id: a.dossier_id }, resume: "Le rattacher au même dossier.", libelleType: null }); }),
      mettre_de_cote: () => lien.mettreDeCote(c.ref, a.raison).then(() => toast.success("Mis de côté. Tu peux le remettre à faire.")),
      remettre: () => lien.remettre(c.ref).then(() => toast.success("Remis à faire.")),
      // T-406 : Matthieu transmet (le serveur choisit Chloé ou Joey d'après l'étape du dossier) ; Chloé et Joey ouvrent l'écran utile, enregistrent « demande faite » (sur clic) ou règlent la carte.
      // T-459 : Matthieu CHOISIT Chloé ou Joey (l'étape du dossier ne fait que présélectionner) ; la carte « Transmis par Matthieu » arrive dans leur Bureau.
      transmettre: () => api.transmettreGroupe([c.ref], a.pour, a.motif, a.note).then((r) => {
        const x = r?.resultats?.[0];
        if (!x?.ok) { toast.error(x?.message || "Transmission impossible."); return; }
        toast.success(x.deja ? "Ce mail était déjà transmis." : `Transmis à ${x.pour === "joey" ? "Joey" : "Chloé"}. La trace est dans l'historique du dossier.`);
        if (!x.deja) setAApprendre({ ref: c.ref, action: "transmettre", params: { pour: a.pour, motif: a.motif }, resume: `Transmettre à ${a.pour === "joey" ? "Joey" : "Chloé"}.`, libelleType: c.type_mail ? c.quoi : null });
      }),
      indesirable: () => marquer([c.ref], "indesirable").then((bons) => { if (bons.length) setAApprendre({ ref: c.ref, action: "indesirable", params: {}, resume: "Le mettre en indésirable.", libelleType: c.type_mail ? c.quoi : null }); }),
      inutile: () => marquer([c.ref], "inutile").then((bons) => { if (bons.length) setAApprendre({ ref: c.ref, action: "inutile", params: {}, resume: "Le classer sans réponse.", libelleType: c.type_mail ? c.quoi : null }); }),
      // T-459 : « Faire pareil » sur une carte qui porte une règle apprise = le MÊME geste, par la même route (donc les mêmes protections) ; le compteur de la règle monte.
      appris_faire: () => faireAppris(c, a.appris),
      ...Object.fromEntries(["ouvrir_pieces", "rediger_dal", "preparer_depot", "assigner", "preparer_demande_avance", "preparer_demande_solde", "ouvrir_dossier"].map((id) => [id,
        () => lien.agir(id, a.dossier_id || c.dossier?.id).then((r) => { if (r?.ouvrir) onOuvrirDossier?.(r.ouvrir.dossier_id, { onglet: r.ouvrir.onglet }); })])),
      demande_avance_faite: () => lien.agir("demande_avance_faite", a.dossier_id || c.dossier?.id).then(() => toast.success("Demande d'avance enregistrée.")),
      demande_solde_faite: () => lien.agir("demande_solde_faite", a.dossier_id || c.dossier?.id).then(() => toast.success("Demande de solde enregistrée.")),
      transmission_faite: () => lien.transmissionFaite(c.id).then(() => toast.success("C'est fait. La trace est dans le dossier.")),
      trancher: () => api.repondreATrancher(c.id, a.version ?? c.bouton?.version, a.choix_id).then(() => toast.success("Merci, Matthieu a compris.")),
      alerte_resolue: () => api.alerteReglee(c.id).then(() => toast.success("Alerte réglée.")),
    }[a.action];
    if (a.action === "modifier") { setFiche(c); return; }
    if (!faire) return;
    setOccupe(true);
    try { await faire(); await rafraichir(); setOuvert(false); if (vue === "tous") setCarteTous(null); }
    catch (e) { toast.error(typeof e.detail === "string" ? e.detail : "Action impossible, réessayez."); }
    finally { setOccupe(false); }
  }, [occupe, rafraichir, vue, lien, onOuvrirDossier, marquer, faireAppris]);

  // Raccourcis : j / k (carte suivante / précédente), Entrée (bouton principal), m (mettre de côté), e (modifier). Jamais dans un champ de saisie.
  useEffect(() => {
    const touche = (e) => {
      const cible = e.target;
      if (cible && (cible.tagName === "INPUT" || cible.tagName === "TEXTAREA" || cible.tagName === "SELECT" || cible.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey || fiche || parler || demande || mailVu) return;
      const c = liste[Math.min(focus, liste.length - 1)];
      if (e.key === "ArrowDown" && grand) { setFocus((f) => Math.min(f + 1, Math.max(liste.length - 1, 0))); e.preventDefault(); }
      else if (e.key === "ArrowUp" && grand) { setFocus((f) => Math.max(f - 1, 0)); e.preventDefault(); }
      else if (e.key === "j") { setFocus((f) => Math.min(f + 1, Math.max(liste.length - 1, 0))); e.preventDefault(); }
      else if (e.key === "k") { setFocus((f) => Math.max(f - 1, 0)); e.preventDefault(); }
      else if (c && e.key === "Enter" && c.bouton && !(c.bouton.action === "envoyer" && c.bouton.destinataire_a_confirmer) && c.bouton.action !== "trancher") { agir(c, c.bouton); e.preventDefault(); }
      else if (c && estMatthieu && e.key === "m" && c.case === "a_faire") { agir(c, { action: "mettre_de_cote" }); e.preventDefault(); }
      else if (c && e.key === "e" && c.secondaire?.action === "modifier") { agir(c, c.secondaire); e.preventDefault(); }
    };
    window.addEventListener("keydown", touche);
    return () => window.removeEventListener("keydown", touche);
  }, [liste, focus, agir, fiche, parler, demande, mailVu, estMatthieu, grand]);

  useEffect(() => { setFocus(0); setOuvert(false); }, [case_]);

  // T-438b : la vue partagée prend la hauteur de l'écran ; la liste et le détail défilent CHACUN dans leur colonne (un `sticky` ne collait à rien :
  // le <main> du hub est en overflow-auto sans jamais défiler, il capte le sticky). La carte choisie reste visible dans la liste.
  useEffect(() => {
    if (!grand) { setHauteur(null); return undefined; }
    const mesurer = () => {
      const el = refSplit.current;
      if (el) setHauteur(Math.max(HAUTEUR_MIN, Math.round(window.innerHeight - el.getBoundingClientRect().top - 16)));
    };
    mesurer();
    window.addEventListener("resize", mesurer);
    return () => window.removeEventListener("resize", mesurer);
  }, [grand, donnees, case_]);
  useEffect(() => {
    const el = refSplit.current?.querySelector('[data-testid="bureau-simple-colonne-liste"] [data-focus="oui"]');
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [focus, case_, donnees]);

  const carteOuverte = choisie && (
    <Carte key={choisie.ref} c={choisie} focus onFocus={() => {}} onAction={agir} occupe={occupe} onOuvrirDossier={onOuvrirDossier} onVoirMail={setMailVu} agent={agent} nomAgent={nom} motifs={donnees?.motifs || []} onDemander={setDemande} />
  );
  const detailPleinEcran = !grand && ouvert && Boolean(choisie) && (!estMatthieu || vue === "bureau");   // téléphone : le détail prend l'écran
  const enBureau = !estMatthieu || vue === "bureau";
  const ligneTous = estMatthieu && vue === "tous" && carteTous ? (tous.donnees?.toutes || []).find((x) => x.ref === carteTous) : null;
  const carteDeTous = ligneTous && (
    <Carte key={ligneTous.ref} c={ligneTous} focus onFocus={() => {}} onAction={agir} occupe={occupe} onOuvrirDossier={onOuvrirDossier} onVoirMail={setMailVu} agent={agent} nomAgent={nom} motifs={donnees?.motifs || []} onDemander={setDemande} />
  );
  const tousPleinEcran = !grand && Boolean(carteDeTous);

  return (
    <div data-testid="bureau-simple" className="oh-bm-racine" style={{ display: "grid", gap: "var(--oh-space-4)" }}>
      {!detailPleinEcran && (<>
      <div className="flex flex-wrap items-start gap-3">
        <div style={{ flex: "1 1 280px", minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 500, lineHeight: 1.4, color: "var(--oh-text)" }} data-testid="bureau-simple-phrase-jour">
            <span className="sr-only">Bureau de {nom} : </span>{donnees ? phraseDuJour(compteurs.a_faire, donnees.a_faire, agent) : `${nom} prépare la liste…`}
          </h1>
          {/* T-406 : la compétence de l'agent, en une phrase, en tête de son Bureau (source unique : le registre du serveur) */}
          {donnees?.agent?.competence && <p style={{ margin: "4px 0 0", fontSize: 15, lineHeight: 1.6, color: "var(--oh-text)" }} data-testid="bureau-simple-competence">{nom} s'occupe {donnees.agent.competence}</p>}
          <p style={{ ...muted, margin: "4px 0 0" }} data-testid="bureau-simple-phrase">{estMatthieu ? PHRASE_ACCUEIL : "Ici, tu valides. Rien ne part sans ton clic."}</p>
          {donnees?.phrase_envoi && <p style={{ ...muted, margin: "4px 0 0" }} data-testid="bureau-simple-envoi">{donnees.phrase_envoi}</p>}
        </div>
        <Bouton type="secondaire" data-testid="bureau-simple-parler" onClick={() => setParler(true)}>
          <MessageSquare className="w-4 h-4 inline mr-1" aria-hidden="true" />Demander à {nom}
        </Bouton>
      </div>
      {parler && <ParlerAMatthieu agent={agent} cible={{ type: "general" }} titre={`Demander à ${nom}`} onFerme={() => setParler(false)} />}
      {demande && (
        <ParlerAMatthieu agent={agent} titre={`Demander à ${nom}`} onFerme={() => setDemande(null)}
          cible={estMatthieu ? { type: "fiche", id: demande.id } : { type: "dossier", id: demande.dossier?.id }} />
      )}

      {erreur && (
        <OhStatusNote variant="error" data-testid="bureau-simple-erreur" title="Chargement impossible">
          <div className="flex flex-wrap items-center gap-2"><span>{erreur}</span>
            <Bouton data-testid="bureau-simple-recharger" onClick={charger}><RefreshCw className="w-4 h-4 inline mr-1" aria-hidden="true" />Réessayer</Bouton></div>
        </OhStatusNote>
      )}

      {estMatthieu && (
        <div role="tablist" aria-label="Vues du Bureau de Matthieu" className="flex flex-wrap items-center gap-2" data-testid="bureau-vues">
          {[["bureau", "Mon Bureau"], ["tous", "Tous les mails"], ["appris", "Ce que Matthieu a appris"]].map(([cle, libelle]) => (
            <Bouton key={cle} type={vue === cle ? "secondaire" : "discret"} role="tab" aria-selected={vue === cle} data-testid={`bureau-vue-${cle}`} style={{ height: 44 }}
              onClick={() => { setVue(cle); setCarteTous(null); setGroupe(null); }}>{libelle}</Bouton>
          ))}
        </div>
      )}
      <LigneApprendre propose={aApprendre} occupe={occupe} onOui={apprendre} onNon={() => setAApprendre(null)} />

      {enBureau && (
      <div role="tablist" aria-label="Les trois cases du Bureau" style={{ display: "grid", gap: "var(--oh-space-3)", gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        {CASES.map(({ cle, compteur, libelle, court, icone: Icone }) => {
          const actif = case_ === cle;
          return (
            <button key={cle} type="button" role="tab" aria-selected={actif} data-testid={`bureau-simple-case-${cle}`} aria-label={`${libelle} : ${donnees ? compteurs[compteur] ?? 0 : "…"}`} title={libelle} onClick={() => setCase(cle)}
              style={{ textAlign: "left", cursor: "pointer", font: "inherit", padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-card)", minHeight: 72, overflowWrap: "normal", wordBreak: "keep-all", minWidth: 0,
                background: actif ? "var(--oh-accent-bg)" : "var(--oh-bg-elevated)", color: actif ? "var(--oh-accent-text)" : "var(--oh-text)",
                border: `${actif ? 1 : 0.5}px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}` }}>
              <span className="flex flex-wrap items-center" style={{ fontSize: 15, fontWeight: 500, columnGap: "var(--oh-space-2, 8px)", rowGap: "var(--oh-space-1, 4px)" }}><Icone className="w-5 h-5 shrink-0" aria-hidden="true" /><span data-testid={`bureau-simple-titre-${cle}`}>{court}</span></span>
              <span style={{ display: "block", fontSize: 32, fontWeight: 500, lineHeight: 1.2 }} data-testid={`bureau-simple-nombre-${cle}`}>{donnees ? compteurs[compteur] ?? 0 : "…"}</span>
            </button>
          );
        })}
      </div>
      )}

      </>)}

      {enBureau && (
      <section aria-label={CASES.find((c) => c.cle === case_).libelle} aria-busy={!donnees && !erreur} data-testid="bureau-simple-liste">
        {detailPleinEcran ? (
          <div data-testid="bureau-simple-plein-ecran" style={{ display: "grid", gap: "var(--oh-space-3)" }}>
            <div><Bouton type="discret" data-testid="bureau-simple-retour" onClick={() => setOuvert(false)}><ArrowLeft className="w-4 h-4 inline mr-1" aria-hidden="true" />Retour</Bouton></div>
            <ul style={{ padding: 0, margin: 0 }}>{carteOuverte}</ul>
          </div>
        ) : !donnees && !erreur ? (
          <p style={muted} data-testid="bureau-simple-chargement">{nom} prépare la liste…</p>
        ) : (donnees?.[case_] || []).length === 0 ? (
          <div data-testid="bureau-simple-vide" style={{ padding: "var(--oh-space-8) var(--oh-space-4)", textAlign: "center", border: "1px dashed var(--oh-border)", borderRadius: "var(--oh-radius-card)", color: "var(--oh-text)", fontSize: 16 }}>
            {phraseVide(case_, compteurs, donnees?.phrase_vide)}
          </div>
        ) : (
          <div ref={refSplit} data-testid="bureau-simple-split" data-mode={grand ? "partage" : "liste"}
            style={grand ? { display: "grid", gridTemplateColumns: "minmax(0, 40fr) minmax(0, 60fr)", gap: "var(--oh-space-4)", alignItems: "start" } : undefined}>
            <div data-testid="bureau-simple-colonne-liste" style={{ minWidth: 0, ...(grand && hauteur ? { height: hauteur, overflowY: "auto", paddingRight: "var(--oh-space-2)" } : {}) }}>
              {groupes ? (
                <div style={{ display: "grid", gap: "var(--oh-space-6, 24px)" }}>
                  {groupes.map((g) => {
                    const ferme = Boolean(replies[clePli(g)]);
                    const Icone = g.icone;
                    return (
                      <div key={g.cle} role="group" aria-labelledby={`bureau-groupe-${g.cle}`} data-testid={`bureau-groupe-${g.cle}`}>
                        <h2 id={`bureau-groupe-${g.cle}`} style={{ margin: "0 0 var(--oh-space-2, 8px)", fontSize: 16, fontWeight: 500, color: "var(--oh-text)" }}>
                          <button type="button" aria-expanded={!ferme} data-testid={`bureau-groupe-bascule-${g.cle}`} onClick={() => setReplies((r) => ({ ...r, [clePli(g)]: !r[clePli(g)] }))}
                            style={{ all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "var(--oh-space-2, 8px)", minHeight: 32 }}>
                            {ferme ? <ChevronRight className="w-4 h-4" aria-hidden="true" /> : <ChevronDown className="w-4 h-4" aria-hidden="true" />}
                            {Icone && <Icone className="w-4 h-4" aria-hidden="true" />}
                            <span>{g.titre} <span style={{ ...muted, fontSize: 14 }}>({g.cartes.length})</span></span>
                          </button>
                        </h2>
                        {!ferme && (
                          <ul style={{ padding: 0, margin: 0, display: "grid", gap: "var(--oh-space-3)" }}>
                            {g.cartes.map((c) => { const i = liste.indexOf(c); return <Ligne key={c.ref} c={c} nature={g} actif={i === focus} onChoisir={() => { setFocus(i); setOuvert(true); }} />; })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <ul style={{ padding: 0, margin: 0, display: "grid", gap: "var(--oh-space-3)" }}>
                  {liste.map((c, i) => <Ligne key={c.ref} c={c} nature={natureDe(c)} actif={i === focus} onChoisir={() => { setFocus(i); setOuvert(true); }} />)}
                </ul>
              )}
            </div>
            {grand && (
              <div data-testid="bureau-simple-detail-colonne" style={{ minWidth: 0, ...(hauteur ? { height: hauteur, overflowY: "auto" } : {}) }}>
                {choisie ? <ul style={{ padding: 0, margin: 0 }}>{carteOuverte}</ul> : <p style={muted} data-testid="bureau-simple-aucune-selection">Choisis une carte dans la liste.</p>}
              </div>
            )}
          </div>
        )}
        {case_ === "de_cote" && donnees?.partiel?.de_cote && <p style={{ ...muted, marginTop: 8 }} data-testid="bureau-simple-partiel">Ce nombre est partiel : seuls les mails les plus récents de l'agent Webmail sont comptés.</p>}
        {donnees?.tronque?.[case_] && <p style={{ ...muted, marginTop: 8 }}>Seuls les 100 premiers sont affichés.</p>}
      </section>
      )}

      {estMatthieu && vue === "appris" && <MatthieuAppris />}
      {estMatthieu && vue === "tous" && (
        tousPleinEcran ? (
          <div data-testid="bureau-tous-plein-ecran" style={{ display: "grid", gap: "var(--oh-space-3)" }}>
            <div><RetourListe onClick={() => setCarteTous(null)} /></div>
            <ul style={{ padding: 0, margin: 0 }}>{carteDeTous}</ul>
          </div>
        ) : (
          <div data-testid="bureau-tous-split" style={grand && carteDeTous ? { display: "grid", gridTemplateColumns: "minmax(0, 55fr) minmax(0, 45fr)", gap: "var(--oh-space-4)", alignItems: "start" } : undefined}>
            <div style={{ minWidth: 0, display: "grid", gap: "var(--oh-space-3)" }}>
              {groupe?.type === "transmettre" && <PanneauTransmettre refs={groupe.refs} motifs={donnees?.motifs || []} occupe={occupe} onFermer={() => setGroupe(null)} onConfirmer={confirmerGroupe} />}
              {groupe?.type === "rattacher" && <PanneauRattacher nombre={groupe.refs.length} occupe={occupe} onFermer={() => setGroupe(null)} onChoisir={confirmerGroupe} />}
              <TousLesMails toutes={tous.donnees?.toutes} chargement={tous.chargement} erreur={tous.erreur} tronque={tous.donnees?.tronque_toutes} occupe={occupe}
                onRecharger={chargerTous} onGroupe={surGroupe} onOuvrir={(l) => setCarteTous(l.ref)} />
            </div>
            {grand && carteDeTous && <div data-testid="bureau-tous-detail" style={{ minWidth: 0 }}><ul style={{ padding: 0, margin: 0 }}>{carteDeTous}</ul></div>}
          </div>
        )
      )}
      <MailOriginePanneau agent={agent} refCarte={mailVu} onFerme={() => setMailVu(null)} />

      <Dialog open={Boolean(fiche)} onOpenChange={(o) => { if (!o) { setFiche(null); setTexteAppris(null); rafraichir(); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bureau-simple-fiche">
          <DialogHeader><DialogTitle>Modifier la réponse</DialogTitle></DialogHeader>
          {fiche && <FicheAmo id={fiche.id} onRetour={() => { setFiche(null); setTexteAppris(null); rafraichir(); }} onOuvrirWebmail={onOuvrirWebmail} onOuvrirDossier={onOuvrirDossier} onChange={rafraichir} copieCachee={null}
            texteInitial={texteAppris}
            onCorrigee={estMatthieu ? (texte, f) => setAApprendre({ ref: `fiche:${f.id}`, action: "reponse", texte, params: {}, resume: "Proposer la même réponse.", libelleType: null }) : undefined} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
